/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Component, ElementRef, Inject, OnInit} from '@angular/core';
import * as _ from 'lodash';

import {CreativeViewer} from '../../../components/util/creative-viewer';
import {normalizePreviewLanguageSelection, PREVIEW_LANGUAGE_OPTIONS, resolvePreviewLanguageCode} from '../../creative/ad/preview-language';
import {collateCreativePreviewLineItemData} from '../../creative/ad/preview-request-data';
import template from './political-overlay-preview-page.component.html';
import {CreativeResource} from '../../../components/api/resources/resource/creative.resource';
import {LayoutResource} from '../../../components/api/resources/resource/layout.resource';
import {PoliticalAdsResource} from '../../../components/api/resources/resource/political-ads.resource';
import {PreviewResource} from '../../../components/api/resources/resource/preview.resource';
import {WINDOW} from '../../angular-platform-providers';

const EMPTY_TEMPLATE_MESSAGE = '<!-- Default render-dsa template could not be loaded. -->';

@Component({
  standalone: false,
  selector: 'adn-political-overlay-preview-page',
  template
})
export class PoliticalOverlayPreviewPageComponent implements OnInit {
  form = {
    creativeId: '',
    template: ''
  };
  previewLanguageOptions = PREVIEW_LANGUAGE_OPTIONS;
  previewLanguage = 'browser';
  previewHostId = 'politicalOverlayPreviewHost';
  previewTargetId = 'politicalOverlayPreviewTarget';
  previewRenderCount = 0;
  error = '';
  errorMessage: any = null;
  publicError: string | null = null;
  loading = false;
  hasPreview = false;
  templateLoading = true;
  codemirrorReady = false;
  derivedPreviewParams: any = null;
  previewUrl = '';

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    private readonly elementRef: ElementRef<HTMLElement>,
    @Inject(WINDOW) private readonly window: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LayoutResource) private readonly Layout: any,
    @Inject(PreviewResource) private readonly Preview: any,
    @Inject(PoliticalAdsResource) private readonly PoliticalAds: any
  ) {
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.codemirrorReady = true;
    }, 0);

    this.PoliticalAds.getRenderDsaTemplate().then((response: any) => {
      this.form.template = this.extractTemplate(response) || EMPTY_TEMPLATE_MESSAGE;
    }).catch((error: any) => {
      this.form.template = EMPTY_TEMPLATE_MESSAGE;
      this.error = _.get(error, ['data'], 'Unable to load the render-dsa template.');
    }).finally(() => {
      this.templateLoading = false;
    });
  }

  getSelectedLanguageCode(): string {
    return resolvePreviewLanguageCode(this.previewLanguage, this.window.navigator);
  }

  makeOverlayRenderTemplate(layoutRenderTemplate: string): string {
    if ((layoutRenderTemplate || '').indexOf('{{{politicalOverlay}}}') < 0) {
      throw new Error('The selected creative needs a political layout. Its render template does not contain {{{politicalOverlay}}}.');
    }
    const overlayScript = '<template id="politicalOverlay">\n'
      + (this.form.template || '')
      + '\n</template>\n';
    return (layoutRenderTemplate || '').replace('{{{politicalOverlay}}}', overlayScript + '{{{politicalOverlay}}}');
  }

  resetPreviewTarget(): HTMLElement | null {
    this.previewRenderCount++;
    this.previewTargetId = 'politicalOverlayPreviewTarget-' + this.previewRenderCount;

    const previewHost = this.elementRef.nativeElement.querySelector('#' + this.previewHostId);
    if (!previewHost) {
      return null;
    }

    previewHost.innerHTML = '';
    const previewTarget = this.document.createElement('div');
    previewTarget.setAttribute('id', this.previewTargetId);
    previewHost.appendChild(previewTarget);
    return previewTarget;
  }

  render(): void {
    this.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
    this.error = '';
    this.errorMessage = null;
    this.publicError = null;
    this.loading = true;
    this.hasPreview = true;

    this.resetPreviewTarget();

    this.Creative.getHydratedPreview(this.form.creativeId).then((creative: any) => {
      const previewParams = {
        creativeId: creative.id,
        networkId: _.get(creative, ['network', 'id']),
        creativeWidth: creative.width,
        creativeHeight: creative.height,
        layoutType: _.get(creative, ['layout', 'ogLayoutType']) || _.get(creative, ['layout', 'layoutType']) || _.get(creative, ['layout', 'detectedCategory']) || 'IMAGE'
      };
      _.assign(previewParams, collateCreativePreviewLineItemData(creative));

      this.derivedPreviewParams = previewParams;
      this.previewUrl = 'POST ' + '/preview';

      const totalAssets = _.get(creative, 'assets', []).concat(_.get(creative, 'libraryCreativeAssets', []));
      const layoutParameters: Record<string, any> = {};
      _.forEach(creative.layoutParameters, (value: any, key: string) => {
        if (value && value.id && !value.content) {
          layoutParameters[key] = _.pick((_.find(totalAssets, ['id', value.id]) || {}), ['cdnId', 'previewCdnId', 'transcode', 'width', 'height', 'content', 'duration', 'bps']);
        } else {
          layoutParameters[key] = value;
        }
      });

      return this.Layout.get({id: _.get(creative, ['layout', 'id'])}).$promise.then((layout: any) => {
        const renderTemplate = this.makeOverlayRenderTemplate(layout.renderTemplate);
        const apiPreviewData: Record<string, any> = {
          creativeWidth: previewParams.creativeWidth,
          creativeHeight: previewParams.creativeHeight,
          layoutParameters: layoutParameters,
          layoutId: layout.id,
          renderTemplate: renderTemplate
        };
        _.assign(apiPreviewData, _.pick(previewParams, ['lineItemId', 'lineItemCode']));

        return new this.Preview(apiPreviewData).$save().then((data: any) => {
          const previewContainer = this.resetPreviewTarget();
          new CreativeViewer(this.document).viewCreative(previewContainer, {
            creativeContent: data.document,
            creativeWidth: previewParams.creativeWidth,
            creativeHeight: previewParams.creativeHeight,
            layoutType: previewParams.layoutType,
            showBorder: true,
            allowScript: true,
            dataLang: this.getSelectedLanguageCode()
          });
        });
      });
    }).catch((error: any) => {
      this.hasPreview = false;
      this.error = _.get(error, 'message') || _.get(error, ['data', 'text'], 'Unable to generate the political overlay preview.');
    }).finally(() => {
      this.loading = false;
    });
  }

  private extractTemplate(response: any): string {
    if (_.isString(response)) {
      return response;
    }
    return _.get(response, 'template')
      || _.get(response, ['result', 'template'])
      || _.get(response, ['data', 'template'])
      || '';
  }
}
